(() => {
  "use strict";

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const arr = v => Array.isArray(v) ? v : (v == null || v === "" ? [] : [v]);
  const txt = v => String(v ?? "").trim();
  const norm = v => txt(v).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const vals = v => arr(v).flatMap(x => typeof x === "string" ? x.split(/[,;|]/) : [x]).map(txt).filter(Boolean);
  const has = (v, terms) => {
    const hay = vals(v).map(norm);
    return terms.some(t => hay.some(x => x.includes(norm(t))));
  };

  const CONCERNS = {
    breakouts: { label:"Bėrimai ir probleminė oda", terms:["berimai","problemine","akn"] },
    pores: { label:"Poros ir inkštirai", terms:["poros","inkstirai"] },
    pigmentation: { label:"Pigmentacija", terms:["pigment","demes"] },
    dehydration: { label:"Sausumas ir dehidratacija", terms:["sausumas","dehidrat"] },
    redness: { label:"Jautrumas ir paraudimas", terms:["jautrum","paraud"] },
    anti_age: { label:"Anti-age", terms:["anti-age","anti age","rauks","senej"] },
    firmness: { label:"Stangrumas", terms:["stangrum"] },
    texture: { label:"Nelygi tekstūra", terms:["tekstur"] },
    glow: { label:"Glow ir papilkėjusi oda", terms:["glow","papilkej","svytej"] },
    barrier: { label:"Pažeistas odos barjeras", terms:["barjer"] }
  };

  const LEVELS = {
    essential: { label:"Essential", terms:["essential"] },
    optimal: { label:"Optimal", terms:["optimal"] },
    advanced: { label:"Advanced", terms:["advanced"] }
  };

  const STEPS = [
    ["cleanse", ["01","valym","praus"], "Valymas"],
    ["tone", ["02","toniz","esenc"], "Tonizavimas"],
    ["treat", ["03","aktyvioji","aktyvi prieziura","eksfol","rugst","retino"], "Aktyvioji priežiūra"],
    ["serum", ["04","serum"], "Serumas"],
    ["eye", ["05","paaki"], "Paakių priežiūra"],
    ["moisturize", ["06","drek","krem"], "Drėkinimas"],
    ["spf", ["07","spf","saules"], "SPF"]
  ];

  const ROUTINES = {
    essential: {
      am:["cleanse","moisturize","spf"],
      pm:["cleanse","moisturize"]
    },
    optimal: {
      am:["cleanse","serum","moisturize","spf"],
      pm:["cleanse","treat","serum","moisturize"]
    },
    advanced: {
      am:["cleanse","tone","serum","eye","moisturize","spf"],
      pm:["cleanse","tone","treat","serum","eye","moisturize"]
    }
  };

  const stepOf = p => STEPS.find(s => has(p.m.routineStep, s[1]))?.[0] || null;
  const stepLabel = key => STEPS.find(s => s[0] === key)?.[2] || key;

  function timesOf(p) {
    const v = p.m.timeOfUse;
    const am = has(v, ["rytas","ryte","am"]);
    const pm = has(v, ["vakaras","vakare","pm"]);
    if (!txt(v) || (!am && !pm)) return ["am","pm"];
    return [...(am ? ["am"] : []), ...(pm ? ["pm"] : [])];
  }

  function prepare(raw) {
    return raw
      .map(p => ({ ...p, m:p.metafields || {} }))
      .map(p => ({ ...p, step:stepOf(p) }));
  }

  function priority(p) {
    const n = Number(p.m.skinMatchPriority);
    return Number.isFinite(n) ? n : 0;
  }

  function isSupplementary(p) {
    const combined = [p.m.routineStep,p.m.productPurpose,p.m.productGoals,p.m.usageFrequency,p.title].filter(Boolean);
    return has(combined, [
      "papildoma prieziura","kauke","1 karta per savaite","1-2 kart","1–2 kart",
      "2-3 kartus per savaite","2–3 kartus per savaite","prietais"
    ]);
  }

  function routineLevelScore(p, level) {
    if (!txt(p.m.routineLevel)) return 0;
    if (has(p.m.routineLevel, LEVELS[level]?.terms || [level])) return 6;
    if (level === "advanced" && has(p.m.routineLevel, ["optimal"])) return 2;
    if (level === "optimal" && has(p.m.routineLevel, ["essential"])) return 1;
    return -1;
  }

  function eligible(p, period, step) {
    if (!p.available || !p.variantId || !p.step || p.step !== step) return false;
    if (!timesOf(p).includes(period)) return false;
    if (isSupplementary(p)) return false;

    // Routine Finder has no personal sensitivity/experience answers.
    // Be conservative: explicit "advanced-only" products do not become default
    // choices unless the catalogue also marks them for beginners.
    if (
      has(p.m.experienceLevel, ["pazeng"]) &&
      !has(p.m.experienceLevel, ["praded"]) &&
      step === "treat"
    ) return false;

    return true;
  }

  function score(p, concern, level, period, step) {
    if (!eligible(p, period, step)) return -Infinity;

    let s = 7;
    const terms = CONCERNS[concern]?.terms || [];

    if (has(p.m.skinConcerns, terms)) s += 14;
    if (has(p.m.productGoals, terms)) s += 11;
    if (has(p.m.productPurpose, terms)) s += 6;

    s += routineLevelScore(p, level);

    if (txt(p.m.timeOfUse)) s += 2;

    // Routine Finder cannot know sensitivity. Mild preference only; never
    // assume that blank means unsuitable.
    if (p.m.sensitiveSkin === true) s += 1;

    // Merchant priority remains bounded.
    s += Math.max(-3, Math.min(5, priority(p)));

    return s;
  }

  function conflict(a, b) {
    const aAvoid = vals(a.m.incompatibleActives).map(norm);
    const bAvoid = vals(b.m.incompatibleActives).map(norm);
    const aAct = vals(a.m.keyActives).map(norm);
    const bAct = vals(b.m.keyActives).map(norm);
    const hit = (avoid, acts) => avoid.some(x => acts.some(y => y.includes(x) || x.includes(y)));
    return hit(aAvoid, bAct) || hit(bAvoid, aAct);
  }

  function pairingBonus(candidate, selected) {
    const ids = new Set(selected.map(x => Number(x.id)));
    if (arr(candidate.m.recommendedPairings).some(x => ids.has(Number(x?.id)))) return 5;
    return selected.some(p => arr(p.m.recommendedPairings).some(x => Number(x?.id) === Number(candidate.id))) ? 5 : 0;
  }

  function choose(products, concern, level, period, step, selected) {
    return products
      .map(p => {
        let s = score(p, concern, level, period, step);
        if (selected.some(x => conflict(p, x))) s -= 100;

        // Routine Finder deliberately avoids stacking multiple treatment-like
        // actives because it lacks individual tolerance information.
        if (p.step === "treat" && selected.some(x => x.step === "treat")) s -= 50;

        s += pairingBonus(p, selected);
        return { p, s };
      })
      .filter(x => Number.isFinite(x.s))
      .sort((a,b) => b.s - a.s || priority(b.p) - priority(a.p) || a.p.price - b.p.price)[0]?.p || null;
  }

  function build(products, concern, level, period) {
    const selected = [];
    for (const step of ROUTINES[level]?.[period] || ROUTINES.optimal[period]) {
      const p = choose(products, concern, level, period, step, selected);
      if (p && !selected.some(x => x.id === p.id)) selected.push(p);
    }
    return selected;
  }

  const unique = (am, pm) => [...new Map([...am,...pm].map(p => [p.variantId,p])).values()];

  function money(cents, root) {
    try {
      return new Intl.NumberFormat("lt-LT", {
        style:"currency",
        currency:root.dataset.currency || "EUR"
      }).format((Number(cents) || 0) / 100);
    } catch {
      return `${((Number(cents) || 0) / 100).toFixed(2)} €`;
    }
  }

  function reasonFor(p, concern, level) {
    const reasons = [];
    const terms = CONCERNS[concern]?.terms || [];

    if (has(p.m.skinConcerns, terms) || has(p.m.productGoals, terms)) {
      reasons.push(`atitinka ${CONCERNS[concern]?.label.toLowerCase() || "pasirinktą poreikį"}`);
    }
    if (has(p.m.routineLevel, LEVELS[level]?.terms || [level])) {
      reasons.push(`tinka ${LEVELS[level]?.label || level} rutinai`);
    }
    if (!reasons.length) reasons.push("geriausiai atitinka šį rutinos etapą pagal SABO produkto duomenis");

    return `Parinktas, nes ${reasons.slice(0,2).join(" ir ")}.`;
  }

  function init(root) {
    if (root.dataset.rfReady) return;
    root.dataset.rfReady = "1";

    let raw = [];
    try {
      raw = JSON.parse($("[data-rf-products]", root)?.textContent || "[]");
    } catch (e) {
      console.error("SABO Routine Finder product JSON error", e);
    }

    const products = prepare(raw);
    const concernInputs = $$('input[name^="routine-finder-concern-"]', root);
    const levelInputs = $$('input[name^="routine-finder-level-"]', root);
    const generate = $("[data-rf-generate]", root);
    const finder = $("[data-rf-finder]", root);
    const results = $("[data-rf-results]", root);

    let state = { concern:null, level:null };

    const selectedConcern = () => concernInputs.find(i => i.checked);
    const selectedLevel = () => levelInputs.find(i => i.checked);

    function syncState() {
      const c = selectedConcern();
      const l = selectedLevel();
      state.concern = c?.value || null;
      state.level = l?.value || null;
      generate.disabled = !(state.concern && state.level);
    }

    root.addEventListener("change", e => {
      if (!e.target.matches('input[type="radio"]')) return;
      syncState();
    });

    generate?.addEventListener("click", () => {
      syncState();
      if (!state.concern || !state.level) return;
      render();
    });

    $("[data-rf-change]", root)?.addEventListener("click", () => {
      results.hidden = true;
      finder.hidden = false;
      finder.scrollIntoView({ behavior:"smooth", block:"start" });
    });

    function render() {
      let am = build(products, state.concern, state.level, "am");
      let pm = build(products, state.concern, state.level, "pm");

      finder.hidden = true;
      results.hidden = false;

      const cLabel = selectedConcern()?.dataset.label || CONCERNS[state.concern]?.label || "";
      const lLabel = selectedLevel()?.dataset.label || LEVELS[state.level]?.label || "";

      $("[data-rf-profile-concern]", root).textContent = cLabel;
      $("[data-rf-profile-level]", root).textContent = lLabel;

      const title = $("[data-rf-results-title]", root);
      if (title) title.textContent = `${cLabel}: ${lLabel} rutina`;

      const copy = $("[data-rf-results-copy]", root);
      if (copy) {
        copy.textContent = `SABO parinkta rytinės ir vakarinės priežiūros kryptis, orientuota į ${cLabel.toLowerCase()}.`;
      }

      renderPeriod("am", am);
      renderPeriod("pm", pm);

      const all = unique(am, pm);
      const total = all.reduce((sum,p) => sum + Number(p.price || 0), 0);
      $("[data-rf-total]", root).textContent = money(total, root);

      const add = $("[data-rf-add-all]", root);
      add.disabled = !all.length;
      add.onclick = () => addCart(all);

      results.scrollIntoView({ behavior:"smooth", block:"start" });
    }

    function renderPeriod(period, list) {
      const box = $(`[data-rf-${period}-products]`, root);
      const empty = $(`[data-rf-${period}-empty]`, root);
      box.innerHTML = "";
      empty.hidden = !!list.length;

      list.forEach((p, i) => {
        box.appendChild(productCard(p, i));
      });
    }

    function productCard(p, index) {
      const frag = $("[data-rf-product-template]", root).content.cloneNode(true);
      $$("[data-rf-product-link]", frag).forEach(a => a.href = p.url);

      $("[data-rf-step-number]", frag).textContent = String(index + 1).padStart(2, "0");
      $("[data-rf-step-name]", frag).textContent = stepLabel(p.step);
      $("[data-rf-product-vendor]", frag).textContent = p.vendor || "";
      $("[data-rf-product-title]", frag).textContent = p.title;
      $("[data-rf-product-reason]", frag).textContent = reasonFor(p, state.concern, state.level);
      $("[data-rf-product-price]", frag).textContent = money(p.price, root);

      const image = $("[data-rf-product-image]", frag);
      if (p.image) {
        image.src = p.image;
        image.alt = p.title;
      } else {
        image.closest(".srf-product__media").hidden = true;
      }

      return frag;
    }

    async function addCart(items) {
      const button = $("[data-rf-add-all]", root);
      const feedback = $("[data-rf-cart-feedback]", root);
      button.disabled = true;

      try {
        const response = await fetch("/cart/add.js", {
          method:"POST",
          headers:{
            "Content-Type":"application/json",
            "Accept":"application/json"
          },
          body:JSON.stringify({
            items:items.map(p => ({ id:p.variantId, quantity:1 }))
          })
        });

        if (!response.ok) throw new Error("Cart add failed");

        feedback.textContent = "Visa rutina pridėta į krepšelį.";
        feedback.hidden = false;
        window.location.assign("/cart");
      } catch (e) {
        feedback.textContent = "Nepavyko pridėti visos rutinos. Pabandyk dar kartą.";
        feedback.hidden = false;
        button.disabled = false;
      }
    }

    syncState();
  }

  const boot = () => $$("[data-routine-finder]").forEach(init);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", boot);
})();